import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { XStack, YStack } from 'tamagui';

import { QRCodeSection, AppVersion, BaseStationAdressSection, DevOptions } from './components';
import { StyledCard } from './components/styles';
import { DraggableScrollView, KeyboardInput } from '../../components/common';
import { useAppSync } from '../../contexts/AppSyncContext';

export enum FormField {
  Street = 'street',
  City = 'city',
  Zip = 'zip',
  State = 'state',
}

export const GeneralSettings: React.FC = () => {
  const {
    updateHubAddress,
    hubUpdating,
    selectedHubId,
    hub: selectedHub,
    getHubById,
  } = useAppSync();
  const { t } = useTranslation();

  const [showKeyboard, setShowKeyboard] = useState(false);
  const [focusedField, setFocusedField] = useState<FormField | null>(null);
  const [keyboardInput, setKeyboardInput] = useState('');

  const [fieldValues, setFieldValues] = useState<Record<FormField, string>>({
    [FormField.Street]: selectedHub?.street || '',
    [FormField.City]: selectedHub?.city || '',
    [FormField.Zip]: selectedHub?.zip || '',
    [FormField.State]: selectedHub?.state || '',
  });

  const keyboardRef = useRef<any>(null);

  const handleInputFocus = (field: FormField) => {
    setFocusedField(field);
    setKeyboardInput(fieldValues[field]);
    setShowKeyboard(true);
  };

  const handleKeyboardChange = (field: FormField, val: string) => {
    setFieldValues((prev) => ({ ...prev, [field]: val }));
  };

  const handleKeyboardSubmit = () => {
    if (focusedField) {
      setFieldValues((prev) => ({ ...prev, [focusedField]: keyboardInput }));
    }
    setShowKeyboard(false);
    setFocusedField(null);
  };

  const handleSave = async () => {
    if (selectedHubId) {
      await updateHubAddress({
        id: selectedHubId,
        street: fieldValues.street,
        city: fieldValues.city,
        state: fieldValues.state,
        zip: fieldValues.zip,
      });
      await getHubById(selectedHubId);
    } else {
      console.warn('No hub selected for address update');
    }
  };

  return (
    <>
      {!showKeyboard && (
        <DraggableScrollView marginLeft={0} paddingVertical={0}>
          <XStack padding="$5" gap="$5">
            <QRCodeSection />
            <BaseStationAdressSection
              addressValue={fieldValues[FormField.Street]}
              cityValue={fieldValues[FormField.City]}
              zipValue={fieldValues[FormField.Zip]}
              stateValue={fieldValues[FormField.State]}
              onFocus={handleInputFocus}
              onChange={handleKeyboardChange}
              onSave={handleSave}
              isSaving={hubUpdating}
            />
            <AppVersion hubId={selectedHubId} hubName={selectedHub?.name} />
            <DevOptions />
          </XStack>
        </DraggableScrollView>
      )}
      {showKeyboard && focusedField && (
        <YStack padding="$5" overflow="visible">
          <StyledCard
            height={432}
            justifyContent="flex-start"
            padding={showKeyboard ? 0 : '$5'}
            overflow="visible">
            <KeyboardInput
              input={keyboardInput}
              onInputChange={setKeyboardInput}
              inputRef={keyboardRef}
              keyboardRef={keyboardRef}
              onKeyboardChange={setKeyboardInput}
              showModal={false}
              onCancel={() => setShowKeyboard(false)}
              buttonLabel={t('common.save')}
              onSubmit={handleKeyboardSubmit}
              placeholder={t(`settings.general.${focusedField}`)}
            />
          </StyledCard>
        </YStack>
      )}
    </>
  );
};
